import { Disclosure } from "@headlessui/react"
import { useForm, yupResolver } from "@mantine/form"
import {
  ArrowLeft,
  Bell,
  CaretUp,
  CheckCircle,
  Files,
  FileText,
  MapPin,
  Network,
  Plus,
  X,
} from "@phosphor-icons/react"
import {
  Breadcrumbs,
  Button,
  Checkbox,
  FormGroup,
  FormInput,
  FormQuerySelector,
  FormSelector,
  RadioGroup,
  Selector,
  SpinnerOverlay,
  TextArea,
  Toast,
} from "@vesatogo/grass-core"
import { FormDateInput } from "@vesatogo/grass-dates"
import { FileUpload } from "@vesatogo/grass-dropzone"
import classNames from "classnames"
import dayjs from "dayjs"
import { omit } from "lodash-es"
import { useEffect } from "react"
import toast from "react-hot-toast"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Address } from "~/components/Map/Address"
import { ALERTS } from "~/constants/Alerts"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import { AppRoutes } from "~/constants/routes"
import {
  useAddUpdateSharedEntityMutation,
  useAddUpdateStorageLocationsMutation,
  useAllSiUnitsQuery,
  useAllStorageKindsQuery,
  useSearchBaseUsersQuery,
  useStorageLocationDetailQuery,
} from "~/generated/graphql"
import { storageLocationModel } from "~/models/storage-location"

const INITIAL_STATE = {
  storage_location: "",
  total_capacity: "",
  unit: "sqft",
  storage_sub_locations: [
    {
      name: "",
      storage_location_kind: null,
      capacity: "",
      capacity_unit: "sqft",
    },
  ],
  current_address: {},
  agreement: {
    attachments: [],
  },
  transaction: {
    attachments: [],
  },
  pocs: [],
  alert: {} as any,
}

const CreateStorageLocation = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id && id !== "new"

  const [{ fetching: loading }, addupdateStorageLocation] =
    useAddUpdateStorageLocationsMutation()
  const [{ data, fetching: loadingDetail }] = useStorageLocationDetailQuery({
    variables: {
      id: id as string,
    },
    pause: !id,
    requestPolicy: "cache-and-network",
  })

  const storageLocation = data?.storage_location_by_pk
  const agreement =
    storageLocation?.current_agreement?.[0] ||
    storageLocation?.pending_agreement?.[0] ||
    storageLocation?.previous_agreements?.[0]
  const transaction = agreement?.agreement_transactions?.[0]

  const isExpired = Boolean(id) && dayjs(agreement?.end_date).isBefore(dayjs())
  const isApproved = agreement?.is_verified
  const isDisabled = isExpired || Boolean(isApproved)

  useEffect(() => {
    if (storageLocation) {
      form.setValues({
        // ...storageLocation,
        storage_location: storageLocation?.name,
        total_capacity: storageLocation?.capacity,
        capacity_unit: {
          ...storageLocation?.capacity_unit,
          shared_id: storageLocation?.capacity_unit?.id,
        },
        storage_sub_locations: storageLocation?.storage_sub_locations?.map(
          sub => {
            return {
              ...sub,
              capacity_unit: {
                ...sub?.capacity_unit,
                shared_id: sub?.capacity_unit?.id,
              },
            }
          }
        ),
        current_address: storageLocation?.address,
        agreement: {
          ...agreement,
          attachments: agreement?.attachments?.map(att => {
            return {
              ...att?.media,
              shared_id: att?.id,
            }
          }),

          lease_tenure_kind: {
            id: agreement?.lease_tenure_kind,
            name: agreement?.lease_tenure_kind,
          },
          ownership_kind: agreement?.ownership_kind,
        },
        pocs:
          storageLocation?.pocs?.map(poc => {
            return {
              ...poc?.user,
              shared_id: poc?.user?.id,
            }
          }) || [],
        transaction: {
          ...agreement?.agreement_transactions?.[0],
          mode_of_payment: transaction?.mode_of_payment
            ? {
                id: transaction?.mode_of_payment,
                name: transaction?.mode_of_payment,
              }
            : undefined,
          attachments: agreement?.agreement_transactions?.[0]?.attachments?.map(
            att => {
              return {
                ...att?.media,
                shared_id: att?.id,
              }
            }
          ),
        },
        alert: ALERTS?.find(
          alert => alert.days === agreement?.expiry_alert_days
        ),
      })
    }
  }, [data])

  const form = useForm<any>({
    initialValues: INITIAL_STATE,
    validate: yupResolver(storageLocationModel),
  })

  const [{ fetching: sharedEntityFetching }, upsertSharedEntity] =
    useAddUpdateSharedEntityMutation()

  const {
    values: state,
    setValues: setState,
    getInputProps: getFormInputProps,
  } = form

  function inputProps(name: string, group?: SharedEntityGroup) {
    const props = getFormInputProps(name)
    return {
      ...props,
      disabled: isDisabled,
      onChange: async (e: any) => {
        if (group) {
          if (Array.isArray(e)) {
            const newValues = await Promise.all(
              e.map(async item => {
                if (item?.codename && item?.name && !item?.shared_id) {
                  const { data } = await upsertSharedEntity({
                    reference_id: item.codename,
                    group_name: group,
                    name: item.name,
                  })
                  return {
                    ...item,
                    shared_id: data?.insert_setu_trade_shared_entity_one?.id,
                  }
                }
                return item
              })
            )
            form.setFieldValue(name, newValues)
          } else if (e?.codename && e?.name && !e?.shared_id) {
            const { data } = await upsertSharedEntity({
              reference_id: e.codename,
              group_name: group,
              name: e.name,
            })
            form.setFieldValue(name, {
              ...e,
              shared_id: data?.insert_setu_trade_shared_entity_one?.id,
            })
          } else {
            props.onChange(e)
          }
        } else {
          props.onChange(e)
        }
      },
    }
  }
  const addSubLocation = () => {
    form.insertListItem("storage_sub_locations", {
      name: "",
      storage_location_kind: null,
      capacity: "",
      capacity_unit: "sqft",
    })
  }

  const removeSubLocation = (index: number) => {
    form.removeListItem("storage_sub_locations", index)
  }

  function getFileUploadProps(
    key: string,
    maxFiles = 4,
    shouldEncrypt = false
  ) {
    const inputProps = form.getInputProps(key)
    return {
      media: inputProps.value,
      onChange: inputProps.onChange,
      maxFiles,
      shouldEncrypt,
      disabled: isDisabled,
      onError: toast.error,
    }
  }

  const handleSubmit = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      toast.custom(
        <Toast
          title="Please fix validation errors"
          intent="danger"
          subtitle={Object.values(validation.errors)?.[0]}
        />
      )
      return
    }
    const location = state.current_address?.location?.coordinates
    const { error } = await addupdateStorageLocation({
      input: {
        id: id,
        name: state.storage_location,
        capacity: Number(state.total_capacity) as any,
        capacity_unit_id: state.capacity_unit?.shared_id,
        sub_locations: state.storage_sub_locations
          ?.filter(sub => sub?.capacity_unit?.shared_id)
          .map(sub_location => {
            return {
              id: sub_location?.id,
              name: sub_location.name,
              // storage_type: sub_location.storage_location_kind,
              capacity: Number(sub_location.capacity),
              capacity_unit_id: sub_location.capacity_unit?.shared_id,
              kind_id: sub_location.storage_location_kind?.id,
            }
          }),
        address:
          state.current_address &&
          omit(
            {
              ...state.current_address,
              id: state.current_address?.id,
              location: location
                ? `POINT (${location[0]} ${location[1]})`
                : undefined,
              district:
                state?.current_address?.district?.name ||
                state?.current_address?.district,
              taluka:
                state?.current_address?.taluka?.name ||
                state?.current_address?.taluka,
              sub_region_id: state?.current_address?.sub_region?.id,
              raw_address: state?.current_address?.raw_address || "",
              name:
                state?.current_address?.name || state?.current_address?.village,
            },
            ["__typename", "short_address", "sub_region"]
          ),
        agreement: {
          agreement_no: state?.agreement?.agreement_no,
          attachments_ids: state?.agreement?.attachments?.map(
            (attachment: any) => attachment?.id
          ),
          expiry_alert_days: state?.alert?.days,
          id: state?.agreement?.id,
          lease_amount: Number(state?.agreement?.lease_amount),
          lease_tenure_kind: state?.agreement?.lease_tenure_kind?.id
            ? state?.agreement?.lease_tenure_kind?.id
            : undefined,
          name: state?.agreement?.name,
          ownership_kind: state?.agreement?.ownership_kind,
          start_date: dayjs(state?.agreement?.start_date).format("YYYY-MM-DD"),
          end_date: dayjs(state?.agreement?.end_date).format("YYYY-MM-DD"),
          tnc: state?.agreement?.tnc,
          transactions: state?.transaction
            ? [
                {
                  account_holder_name: state?.transaction?.account_holder_name,
                  account_number: state?.transaction?.account_number,
                  bank_name: state?.transaction?.bank_name,
                  ifsc_code: state?.transaction?.ifsc_code,
                  mode_of_payment: state?.transaction?.mode_of_payment?.id,
                  attachments_ids: state?.transaction?.attachments?.map(
                    (attachment: any) => attachment?.id
                  ),
                  amount: Number(state?.transaction?.amount),
                  id: state?.transaction?.id,
                  transaction_id: state?.transaction?.transaction_id,
                },
              ]
            : [],
        },

        pocs_ids: state?.pocs?.map((poc: any) => poc?.shared_id),
      },
    })

    if (error) {
      toast.custom(
        <Toast title="Error" subtitle={error.message} intent="danger" />
      )
    } else {
      toast.custom(
        <Toast
          title="Success"
          subtitle="Storage Location created successfully"
        />
      )
      navigate(AppRoutes.storageLocations)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-20">
      <header className="bg-white p-3 border-b border-gray-200 flex items-center gap-4">
        <SpinnerOverlay show={loadingDetail} />
        <button
          onClick={() => navigate(-1)}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <Breadcrumbs
          items={[
            {
              text: "Storage Location",
              link: AppRoutes.storageLocations,
              isActive: false,
            },
            {
              text: isEdit
                ? `Edit Storage Location ${id}`
                : "Create Storage Location",
              link: isEdit
                ? `${AppRoutes.storageLocationDetail(id as any)}/edit`
                : AppRoutes.createStorageLocation,
              isActive: true,
            },
          ]}
          linkRenderer={(link, item) => {
            return <Link to={link as any}>{item}</Link>
          }}
        />
      </header>

      <main className="flex justify-between gap-3 max-w-6xl mx-auto w-full p-4 bg-white mt-6 rounded-xl shadow-sm relative">
        <div className="flex flex-col gap-6 w-[70%]">
          <Disclosure
            as="div"
            className="border-1 border-gray-300 rounded-xl bg-gray-100 "
            defaultOpen
          >
            <Disclosure.Button className="rounded-xl flex justify-between items-center px-4 py-3 border-b border-gray-100 bg-gray-50 w-full">
              <div className="flex items-center gap-2 text-green">
                <Files size={20} />
                <span className="font-500 ">Storage Info.</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <CaretUp size={18} className="text-gray-800" />
              </div>
            </Disclosure.Button>

            <Disclosure.Panel>
              <div className="p-6 grid grid-cols-2 gap-6 ">
                <FormInput
                  label="Storage Location"
                  required
                  className="col-span-2"
                  {...inputProps("storage_location")}
                />
                <FormInput
                  label="Total Capacity"
                  required
                  {...inputProps("total_capacity")}
                />
                <FormQuerySelector
                  label="Unit"
                  required
                  {...inputProps("capacity_unit", SharedEntityGroup.KiteSiUnit)}
                  dataHook={useAllSiUnitsQuery}
                  variables={{ type: ["Weight", "SKU"] }}
                ></FormQuerySelector>
              </div>

              <div className="m-3 p-3 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-green-400 flex items-center gap-2">
                    <Network size={20} /> Sub Locations
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={addSubLocation}
                    leftIcon={<Plus />}
                    // disabled={isDisabled}
                  >
                    Sub-Location
                  </Button>
                </div>

                <div className="flex flex-col gap-4">
                  {form.values.storage_sub_locations?.map((_, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 border rounded-lg relative bg-gray-100"
                    >
                      <button
                        onClick={() => removeSubLocation(index)}
                        className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                      >
                        <X />
                      </button>
                      <FormInput
                        label="Sub-Location"
                        {...inputProps(`storage_sub_locations.${index}.name`)}
                        disabled={false}
                      />
                      <FormQuerySelector
                        label="Storage Type"
                        {...inputProps(
                          `storage_sub_locations.${index}.storage_location_kind`
                        )}
                        disabled={false}
                        dataHook={useAllStorageKindsQuery}
                      />
                      <FormInput
                        label="Capacity"
                        {...inputProps(
                          `storage_sub_locations.${index}.capacity`
                        )}
                        disabled={false}
                      />
                      <FormQuerySelector
                        label="Unit"
                        {...inputProps(
                          `storage_sub_locations.${index}.capacity_unit`,
                          SharedEntityGroup.KiteSiUnit
                        )}
                        dataHook={useAllSiUnitsQuery}
                        variables={{ type: ["Weight", "SKU"] }}
                        disabled={false}
                      />
                    </div>
                  ))}
                  <div
                    onClick={addSubLocation}
                    className="p-8 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-400 gap-2"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <Plus />
                    </div>
                    <span className="text-sm">
                      Add more zones, areas, or sections
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-6 pb-6 border-t pt-6">
                <FormQuerySelector
                  label="POC"
                  {...inputProps("pocs", SharedEntityGroup.KiteUser)}
                  placeholder="Select"
                  dataHook={useSearchBaseUsersQuery}
                  serverSideQuery
                  isMulti
                  variables={{
                    groups: ["Farmer"],
                  }}
                  isCreatable
                  getOptionLabel={item => {
                    if (item.__isNew__)
                      return (
                        <div
                          onClick={e => {
                            e.stopPropagation()
                          }}
                        >
                          <Link
                            className="!w-full"
                            to={
                              import.meta.env.VITE_KITE_UI +
                              "/admin/d/farmer/new"
                            }
                            target="_blank"
                            onClick={e => {
                              e.stopPropagation()
                            }}
                          >
                            Register POC
                          </Link>
                        </div>
                      )
                    return item.name
                  }}
                />
              </div>
            </Disclosure.Panel>
          </Disclosure>

          <Disclosure
            as="div"
            className="border-1 border-gray-300 rounded-xl bg-gray-100 overflow-hidden"
            defaultOpen
          >
            <Disclosure.Button className="rounded-xl flex justify-between items-center px-4 py-3 border-b border-gray-100 bg-gray-50 w-full">
              <div className="flex items-center gap-2 text-green">
                <MapPin size={20} />
                <span className="font-500 ">Address Details</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <CaretUp size={18} className="text-gray-800" />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel className={"p-6"}>
              <Address
                form={form}
                setValues={form.setValues}
                getInputProps={getFormInputProps}
                mapClassName="!w-full"
                mapWidth="100%"
                address={state.current_address as any}
                addressKey="current_address"
                formClassName="w-full"
                className={"flex flex-col"}
                disabled={isDisabled}
              />
            </Disclosure.Panel>
          </Disclosure>

          <Disclosure
            as="div"
            className="border-1 border-gray-300 rounded-xl bg-gray-100 overflow-hidden"
            defaultOpen
          >
            <Disclosure.Button className="rounded-xl flex justify-between items-center px-4 py-3 border-b border-gray-100 bg-gray-50 w-full">
              <div className="flex items-center gap-2 text-green">
                <FileText size={20} />
                <span className="font-500">Agreement Details</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <CaretUp size={18} className="text-gray-800" />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel>
              <div className="p-6 flex flex-col gap-6">
                <FormInput
                  label="Name"
                  required
                  {...inputProps("agreement.name")}
                  placeholder="Enter"
                />
                <div className="grid grid-cols-2 gap-6">
                  <FormDateInput
                    required
                    label="Start Date"
                    {...inputProps("agreement.start_date")}
                    placeholder="Select"
                  />
                  <FormDateInput
                    required
                    label="End Date"
                    {...inputProps("agreement.end_date")}
                    placeholder="Select"
                  />

                  <RadioGroup
                    className={"mb-3 flex"}
                    label="Type of Store"
                    {...inputProps("agreement.ownership_kind")}
                  >
                    <Checkbox
                      disabled={isDisabled}
                      type={"radio"}
                      value={"Own"}
                      label={"Own"}
                    />
                    <Checkbox
                      disabled={isDisabled}
                      type={"radio"}
                      value={"Lease"}
                      label={"Rent"}
                    />
                  </RadioGroup>
                  <FormGroup label="Rent Type">
                    <Selector
                      placeholder="Select"
                      options={[
                        { id: "Monthly", name: "Monthly" },
                        { id: "Yearly", name: "Yearly" },
                      ]}
                      {...inputProps("agreement.lease_tenure_kind")}
                    />
                  </FormGroup>
                  <FormInput
                    label="Rent Amount"
                    placeholder="Enter"
                    {...inputProps("agreement.lease_amount")}
                  />
                </div>
                <FormGroup
                  label={
                    <div>
                      Agreement |{" "}
                      <span className="!text-xs text-gray-500">
                        Upload multiple PDF, JPG, or PNG files (maximum size: 10
                        MB)
                      </span>{" "}
                    </div>
                  }
                >
                  <FileUpload
                    {...getFileUploadProps(`agreement.attachments`, 4)}
                    endpoint={`https://${
                      import.meta.env.VITE_REST_URL
                    }/rest/trade/uploader/`}
                    appName="setutrade"
                    onSuccess={(file: any) => {
                      form.setFieldValue("agreement.attachments", [
                        ...state?.agreement?.attachments,
                        file[0]?.data?.media,
                      ] as any)
                    }}
                  />
                </FormGroup>
                <div className="grid grid-cols-2 gap-6">
                  <FormInput
                    label="Agreement No."
                    {...inputProps("agreement.agreement_no")}
                    placeholder="Enter"
                  />
                  <FormInput
                    label="Description"
                    {...inputProps("agreement.description")}
                    placeholder="Enter"
                  />
                </div>
                <FormGroup label="Terms & Conditions">
                  <TextArea
                    className="!w-full"
                    placeholder="Enter"
                    {...inputProps("agreement.tnc")}
                  />
                </FormGroup>
              </div>
            </Disclosure.Panel>
          </Disclosure>

          <Disclosure
            as="div"
            className="border-1 border-gray-300 rounded-xl bg-gray-100"
            defaultOpen
          >
            <Disclosure.Button className="rounded-xl flex justify-between items-center px-4 py-3 border-b border-gray-100 bg-gray-50 w-full">
              <div className="flex items-center gap-2 text-green">
                <FileText size={20} />
                <span className="font-500">Transaction Details</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <CaretUp size={18} className="text-gray-800" />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel>
              <div className="p-6 flex flex-col gap-6">
                <FormGroup>
                  <FileUpload
                    {...getFileUploadProps(`transaction.attachments`, 4)}
                    endpoint={`https://${
                      import.meta.env.VITE_REST_URL
                    }/rest/trade/uploader/`}
                    appName="setutrade"
                    onSuccess={(file: any) => {
                      form.setFieldValue("transaction.attachments", [
                        ...state?.transaction?.attachments,
                        file[0]?.data?.media,
                      ] as any)
                    }}
                  />
                </FormGroup>
                <div className="grid grid-cols-2 gap-6">
                  <FormInput
                    label="Account holders name"
                    {...inputProps("transaction.account_holder_name")}
                  />
                  <FormInput
                    label="Account Number"
                    {...inputProps("transaction.account_number")}
                  />
                  <FormInput
                    label="IFSC Code"
                    {...inputProps("transaction.ifsc_code")}
                  />
                  <FormInput
                    label="Bank"
                    {...inputProps("transaction.bank_name")}
                  />
                  <FormSelector
                    label="Payment Mode"
                    required
                    options={[
                      { id: "UPI", name: "UPI" },
                      { id: "NEFT", name: "NEFT" },
                      { id: "RTGS", name: "RTGS" },
                      { id: "IMPS", name: "IMPS" },
                    ]}
                    {...inputProps("transaction.mode_of_payment")}
                  />
                  <FormInput
                    label="Amount"
                    required
                    {...inputProps("transaction.amount")}
                  />
                  <FormInput
                    label="Transaction ID"
                    required
                    className="col-span-2"
                    {...inputProps("transaction.transaction_id")}
                  />
                </div>
              </div>
            </Disclosure.Panel>
          </Disclosure>
        </div>

        <div className="w-[30%] flex flex-col gap-4 ">
          <div className="p-4 border-gray-300 border rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <Bell size={20} />
              <h3 className="font-500 text-gray-800">Alert Settings</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Select when you want to receive notifications before the agreement
              expires.
            </p>

            <div className="flex flex-col gap-3 w-full">
              {ALERTS.map(alert => {
                const isSelected = alert.id === state?.alert?.id
                return (
                  <div
                    key={alert.id}
                    onClick={() => {
                      form.setFieldValue("alert", alert)
                    }}
                    className={classNames(
                      "p-3 border rounded border-gray-300 rounded-lg flex flex-col gap-1 cursor-pointer",
                      isSelected ? "border-green-150 bg-green-100" : ""
                    )}
                  >
                    <div className="flex items-baseline gap-2">
                      <Checkbox disabled={isDisabled} checked={isSelected} />
                      <div className="text-sm flex flex-col">
                        <span className="font-600">{alert.label}</span>
                        <span className="text-xs text-gray-600">
                          {alert.description}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </main>

      <footer className="fixed bottom-0 border-b-4 border-green-500 left-0 right-0 bg-white p-4 flex justify-between items-center z-20">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="solid"
          onClick={handleSubmit}
          leftIcon={<CheckCircle weight="fill" />}
          loading={loading}
          // disabled={isDisabled}
        >
          {isEdit ? "Update" : "Send for Approval"}
        </Button>
      </footer>
    </div>
  )
}

export default CreateStorageLocation
